// ThesisPage.js - Strategic Investment Theses with LIVE Data
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import Silk from '../components/Silk';
import { CrossAssetTheses } from '../components/CrossAssetTheses';
import HypothesisExplorer from '../components/HypothesisExplorer';
import HypothesisCorrelationLab from '../components/HypothesisCorrelationLab';

import { IndexFamilyRow } from '../components/IndexFamilyRow';
import { IndexCorrelationLab } from '../components/IndexCorrelationLab';
import { IndexTopHoldings } from '../components/IndexTopHoldings';
import { IndexHeroBanner } from '../components/IndexHeroBanner';
import { InvestmentTheme } from '../components/InvestmentTheme';
import { ThesisHeroChart } from '../components/ThesisHeroChart';
//import { InvestmentTheme } from './MarketOverviewPage'; // Import from homepage
import { ELECTION_CLUSTERS, GLOBAL_MACRO_THESES, useTheses } from '../data/thesisData';
import { 
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell 
} from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';

// ============================================
// STRATEGIC THESES (with links to detail pages)
// ============================================
const STRATEGIC_THESES = [
  {
    id: 'warsh-nomination',
    title: '💼 Kevin Warsh Nomination',
    subtitle: 'The "Pragmatic Hawk" & Dollar Strength. Pricing a steeper yield curve.',
    category: 'Monetary Policy',
    conviction: 85,
    hasDetailPage: true,
    link: '/thesis/warsh',
    tickers: ['DXY', 'TLT', 'KRE'],
    color: '#3b82f6'
  },
  {
    id: 'brazil-fiscal',
    title: '🏛️ Brazil Fiscal Drift',
    subtitle: 'Erosion of "Arcabouço" & Curve Steepening. Short BRL exposure.',
    category: 'Emerging Markets',
    conviction: 68,
    hasDetailPage: false,
    link: null,
    tickers: ['EWZ', 'VALE3', 'PETR4'],
    color: '#f97316'
  },
];

// ============================================
// THESIS PERFORMANCE CARD (with live PnL)
// ============================================
const ThesisPerformanceCard = ({ thesis, marketData, theme, onClick }) => {
  // Calculate live PnL from tickers
  const calculatePnL = () => {
    if (!marketData || !thesis.tickers) return 0;
    
    const tickerPerformance = thesis.tickers.map(ticker => {
      const asset = marketData.topGainers?.find(t => t.ticker === ticker) ||
                    marketData.topLosers?.find(t => t.ticker === ticker) ||
                    marketData.mostActive?.find(t => t.ticker === ticker);
      return asset?.var || 0;
    });
    
    return tickerPerformance.length > 0 
      ? tickerPerformance.reduce((a, b) => a + b, 0) / tickerPerformance.length 
      : 0;
  };

  const pnl = calculatePnL();
  const isPositive = pnl >= 0;

  return (
    <div 
      onClick={onClick}
      style={{
        background: theme.cardBg,
        borderRadius: '16px',
        padding: '1.5rem',
        border: `2px solid ${thesis.hasDetailPage ? thesis.color : theme.border}`,
        backdropFilter: 'blur(10px)',
        cursor: thesis.hasDetailPage ? 'pointer' : 'default',
        transition: 'all 0.3s',
        position: 'relative',
        overflow: 'hidden'
      }}
      onMouseEnter={(e) => {
        if (thesis.hasDetailPage) {
          e.currentTarget.style.transform = 'translateY(-4px)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${thesis.color}40`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Gradient overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${thesis.color}20, transparent)`,
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem', position: 'relative', zIndex: 1 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <span style={{
              fontFamily: "'IBM Plex Mono', monospace",
              background: `${thesis.color}20`,
              color: thesis.color,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              border: `1px solid ${thesis.color}40`
            }}>
              {thesis.category}
            </span>
            {thesis.hasDetailPage && (
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                background: 'rgba(212, 175, 55, 0.2)',
                color: '#D4AF37',
                padding: '4px 10px',
                borderRadius: '6px',
                fontSize: '0.65rem',
                fontWeight: 'bold'
              }}>
                VER DETALHES →
              </span>
            )}
          </div>
          <h3 style={{
            fontFamily: "'Roble', system-ui, sans-serif",
            fontSize: '1.3rem',
            fontWeight: 800,
            color: theme.textPrimary,
            marginBottom: '0.5rem',
            lineHeight: '1.2'
          }}>
            {thesis.title}
          </h3>
          <p style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontWeight: 400,
            fontSize: '0.9rem',
            color: theme.textSecondary,
            lineHeight: '1.5'
          }}>
            {thesis.subtitle}
          </p>
        </div>

        {thesis.conviction >= 80 && (
          <span style={{ fontSize: '2rem' }}>🔥</span>
        )}
      </div>

      {/* Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: '1rem',
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'rgba(0,0,0,0.2)',
        borderRadius: '8px',
        position: 'relative',
        zIndex: 1
      }}>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
            Conviction
          </div>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: '1.5rem',
            fontWeight: 600,
            color: thesis.conviction >= 80 ? '#4ade80' : '#facc15'
          }}>
            {thesis.conviction}%
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
            PnL (Live)
          </div>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: '1.5rem',
            fontWeight: 600,
            color: isPositive ? '#4ade80' : '#ef4444'
          }}>
            {isPositive ? '+' : ''}{pnl.toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.7rem', color: theme.textSecondary, textTransform: 'uppercase' }}>
            Assets
          </div>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontSize: '1.5rem',
            fontWeight: 600,
            color: theme.textPrimary
          }}>
            {thesis.tickers.length}
          </div>
        </div>
      </div>

      {/* Tickers */}
      <div style={{
        marginTop: '1rem',
        display: 'flex',
        gap: '0.5rem',
        flexWrap: 'wrap',
        position: 'relative',
        zIndex: 1
      }}>
        {thesis.tickers.map((ticker, i) => (
          <span key={i} style={{
            fontFamily: "'Roble', system-ui, sans-serif",
            fontWeight: 800,
            background: 'rgba(255,255,255,0.05)',
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '0.75rem',
            color: theme.textSecondary,
            border: `1px solid ${theme.border}`
          }}>
            {ticker}
          </span>
        ))}
      </div>
    </div>
  );
};

// ============================================
// THESIS OVERVIEW STATS
// ============================================
const ThesisOverviewStats = ({ marketData, theme }) => {
  const totalTheses = STRATEGIC_THESES.length + ELECTION_CLUSTERS.length + GLOBAL_MACRO_THESES.length;
  const activeTheses = STRATEGIC_THESES.filter(t => t.conviction >= 70).length + 
                       ELECTION_CLUSTERS.filter(c => c.isHero).length;
  
  return (
    <div style={{
      background: `linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(102, 96, 254, 0.1))`,
      border: `1px solid ${theme.border}`,
      borderRadius: '16px',
      padding: '2rem',
      marginBottom: '3rem'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <span style={{ fontSize: '2.5rem' }}>💡</span>
        <div>
          <h2 style={{
            fontFamily: "'Roble', system-ui, sans-serif",
            fontSize: '2.5rem',
            fontWeight: 800,
            color: '#D4AF37',
            margin: 0
          }}>
            Conheça Nossas Teses
          </h2>
          <p style={{
            fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
            fontWeight: 400,
            fontSize: '1.1rem',
            color: theme.textSecondary,
            margin: '0.5rem 0 0'
          }}>
            Análises estratégicas baseadas em dados alternativos, machine learning e inteligência de mercado.
          </p>
        </div>
      </div>

      {/* Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1.5rem',
        marginTop: '2rem'
      }}>
        <div style={{
          padding: '1rem',
          background: 'rgba(212, 175, 55, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(212, 175, 55, 0.3)'
        }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.8rem', color: theme.textSecondary }}>Total de Teses</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '2rem', fontWeight: 600, color: '#D4AF37' }}>{totalTheses}</div>
        </div>
        <div style={{
          padding: '1rem',
          background: 'rgba(74, 222, 128, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(74, 222, 128, 0.3)'
        }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.8rem', color: theme.textSecondary }}>Alta Convicção</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '2rem', fontWeight: 600, color: '#4ade80' }}>{activeTheses}</div>
        </div>
        <div style={{
          padding: '1rem',
          background: 'rgba(59, 130, 246, 0.1)',
          borderRadius: '8px',
          border: '1px solid rgba(59, 130, 246, 0.3)'
        }}>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.8rem', color: theme.textSecondary }}>Páginas Detalhadas</div>
          <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontSize: '2rem', fontWeight: 600, color: '#3b82f6' }}>
            {STRATEGIC_THESES.filter(t => t.hasDetailPage).length}
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================
export default function ThesisPage() {
  const [isDark, setIsDark] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useTheses(); // re-render when live thesis data lands from the API

  const theme = {
    background: isDark ? 'rgba(10, 10, 10, 0.95)' : 'rgba(240, 240, 240, 0.9)',
    textPrimary: isDark ? '#fff' : '#111',
    textSecondary: isDark ? '#a1a1aa' : '#555',
    cardBg: isDark ? 'rgba(20, 20, 20, 0.6)' : 'rgba(255, 255, 255, 0.8)',
    border: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0,0,0,0.1)',
    itemBg: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.03)',
    itemBgHover: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
  };

  // Fetch LIVE market data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/overview`);
        const data = await response.json();
        setMarketData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching market data:', err);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleThesisClick = (thesis) => {
    if (thesis.hasDetailPage && thesis.link) {
      navigate(thesis.link);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: isDark ? '#050505' : '#f0f0f0', color: theme.textPrimary, position: 'relative' }}>
      {/* Background — opacity 0.4 to match home page darkness */}
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', opacity: 0.4 }}>
        <Silk speed={12} scale={2.5} color="#6660fe" noiseIntensity={2} rotation={0} />
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Header theme={theme} isDark={isDark} toggleTheme={() => setIsDark(!isDark)} topTickers={[]} />

        <main style={{ maxWidth: '1400px', margin: '0 auto', padding: 'clamp(1rem, 4vw, 2rem)' }}>
          
          {/* Hero Header */}
          <div style={{ textAlign: 'center', marginBottom: '3rem', marginTop: '2rem' }}>
            <h1 style={{ 
              fontFamily: "'Roble', system-ui, sans-serif",
              fontSize: 'clamp(2rem, 6vw, 4rem)', 
              fontWeight: 800, 
              letterSpacing: '-2px', 
              marginBottom: '1rem',
              background: 'linear-gradient(to right, #fff, #666)', 
              backgroundClip: 'text', 
              WebkitBackgroundClip: 'text', 
              color: 'transparent'
            }}>
              Thesis Central
            </h1>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '1.2rem', color: theme.textSecondary, maxWidth: '700px', margin: '0 auto' }}>
              Strategic Intelligence • Market Regimes • Alpha Generation
            </p>
          </div>

          {/* Overview Stats */}
          <ThesisOverviewStats marketData={marketData} theme={theme} />

          {/* === HERO CHART: Best performing basket === */}
          {!loading && marketData && (
            <ThesisHeroChart
              allTheses={[...ELECTION_CLUSTERS, ...GLOBAL_MACRO_THESES]}
              marketData={marketData}
              theme={theme}
            />
          )}

          {/* === SECTION 1: STRATEGIC THESES === */}
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '2rem' }}>
              <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '2rem', fontWeight: 800, color: theme.textPrimary }}>
                🔥 Teses Estratégicas
              </h2>
              <div style={{ height: '1px', background: theme.border, flex: 1 }} />
              <span style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, fontSize: '0.8rem', color: theme.textSecondary }}>
                {STRATEGIC_THESES.length} Active
              </span>
            </div>

            {loading ? (
              <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
                Carregando dados do mercado...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: '2rem' }}>
                {STRATEGIC_THESES.map((thesis) => (
                  <ThesisPerformanceCard 
                    key={thesis.id}
                    thesis={thesis}
                    marketData={marketData}
                    theme={theme}
                    onClick={() => handleThesisClick(thesis)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* === SECTION 2: ELEIÇÕES 2026 === */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '2rem', fontWeight: 800, color: theme.textPrimary }}>
                🗳️ Eleições 2026 • Bancadas & Políticas
              </h2>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.75rem',
                background: 'rgba(34, 197, 94, 0.2)',
                color: '#22c55e',
                padding: '4px 12px',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                LIVE DATA
              </span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary, marginBottom: '2rem', maxWidth: '800px' }}>
              Clusters de investimento baseados em cenários eleitorais e mudanças regulatórias esperadas.
            </p>

            {loading ? (
              <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
                Carregando dados das teses...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 480px), 1fr))', gap: '1.5rem' }}>
                {ELECTION_CLUSTERS.map((cluster) => (
                  <InvestmentTheme 
                    key={cluster.id}
                    theme={theme}
                    title={cluster.title}
                    subtitle={cluster.subtitle}
                    tickers={cluster.tickers}
                    marketData={marketData}
                  />
                ))}
              </div>
            )}
          </div>

          {/* === SECTION 3: GLOBAL MACRO === */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
          <div style={{ marginBottom: '4rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: "'Roble', system-ui, sans-serif", fontSize: '2rem', fontWeight: 800, color: theme.textPrimary }}>
                🌎 Global Macro & Tendências 2026
              </h2>
              <span style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: '0.75rem',
                background: '#0047AB',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '6px',
                fontWeight: 'bold'
              }}>
                Morgan Stanley Outlook
              </span>
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, color: theme.textSecondary, marginBottom: '2rem', maxWidth: '800px' }}>
              Baseado nas "4 Key Themes & 10 Predictions" para 2026: Difusão da IA, Energia, Mundo Multipolar e Mudanças Sociais.
            </p>

            {loading ? (
              <div style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", fontWeight: 400, textAlign: 'center', padding: '3rem', color: theme.textSecondary }}>
                Carregando teses macro...
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '1.5rem' }}>
                {GLOBAL_MACRO_THESES.map((macro) => (
                  <InvestmentTheme 
                    key={macro.id}
                    theme={theme}
                    title={macro.title}
                    subtitle={macro.subtitle}
                    tickers={macro.tickers}
                    marketData={marketData}
                  />
                ))}
              </div>
            )}
          </div>
           {/* === SECTION 4: CROSS-ASSET EXPERIMENTAL === */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
          <div style={{ marginBottom: '4rem' }}>
            {!loading && marketData && (
              <CrossAssetTheses marketData={marketData} theme={theme} />
            )}
          </div>

        </main>
      </div>
    </div>
  );
}